import React, { useState } from 'react'
import { Plus } from 'lucide-react'
import { HabitService, type Habit, type UpdateHabitRequest, type Weekday, type TimeWindow } from '../../../services/habitService'
import { InlinePopover, ChipRow } from '../../tasks/inline/InlinePopover'
import { ParentPicker, type ParentType } from '../../hierarchy/ParentPicker'
import HabitScheduleEditor, { makeDefaultScheduleValue, type HabitScheduleValue } from '../HabitScheduleEditor'
import { hasSchedule } from '../../../utils/habitSections'
import { scheduleWords, durationWords, reminderWords } from '../../../utils/habitWords'
import { HABIT_PRIORITY_OPTIONS, DURATION_CHIPS } from '../../../utils/habitOptions'
import { scheduleFromHabit } from '../scheduleFromHabit'

/**
 * A habit's facts, each edited where it is shown (Habits UX pass
 * 2026-09-06, the task page's philosophy): the schedule, how long each time
 * takes, the bare minimum, the cue, why it matters, priority, the formation
 * stage, what it belongs to, and the follow-through reminders. With these the
 * edit mode has nothing left to offer and leaves the page.
 */
interface EditorProps {
  habit: Habit
  onChanged: (habit: Habit) => void
  trigger?: React.ReactNode
  variant?: 'dotted' | 'chip' | 'icon'
  className?: string
}

async function write(habit: Habit, payload: UpdateHabitRequest, onChanged: (h: Habit) => void) {
  const svc = new HabitService()
  await svc.updateHabit(String(habit.id), payload)
  onChanged(await svc.getHabit(String(habit.id)))
}

const inputClass = 'w-full rounded-md border border-soft bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/30'

// ── Title ──────────────────────────────────────────────────────────────────

export function HabitTitleEditor({ habit, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [value, setValue] = useState('')
  const reset = () => setValue(habit.title || '')
  const save = async () => {
    const text = value.trim()
    if (!text) throw new Error('A habit needs a name.')
    if (text === (habit.title || '').trim()) return
    await write(habit, { title: text }, onChanged)
  }
  return (
    <InlinePopover label="Title" variant={variant} className={className} onOpen={reset} onSave={save} testId="habit-edit-title" width="w-96" trigger={trigger ?? habit.title}>
      <input type="text" value={value} onChange={(e) => setValue(e.target.value)} aria-label="Title" autoFocus className={inputClass} placeholder="Name the habit as the thing you do — Walk…, Read…, Call…" />
    </InlinePopover>
  )
}

// ── One-line words: why, bare minimum, cue ─────────────────────────────────

function TextFactEditor({ habit, onChanged, trigger, variant = 'dotted', className, field, label, placeholder, current, rows = 2 }: EditorProps & {
  field: 'description' | 'minimum_viable_repetition' | 'cue_or_anchor'
  label: string
  placeholder: string
  current: string | null | undefined
  rows?: number
}) {
  const [text, setText] = useState('')
  const reset = () => setText(current || '')
  const save = async () => {
    const t = text.trim()
    await write(habit, { [field]: t || null } as UpdateHabitRequest, onChanged)
  }
  return (
    <InlinePopover label={label} variant={variant} className={className} onOpen={reset} onSave={save} testId={`habit-edit-${field}`} width="w-96"
                   trigger={trigger ?? (current || <><Plus className="h-3 w-3" /> {label.toLowerCase()}</>)}>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={rows} aria-label={label} autoFocus placeholder={placeholder} className={`${inputClass} resize-y`} />
      {current && <button type="button" onClick={() => setText('')} className="text-app-micro text-muted underline decoration-dotted hover:text-secondary">Clear it</button>}
    </InlinePopover>
  )
}

export function HabitWhyEditor(p: EditorProps) {
  return <TextFactEditor {...p} field="description" label="Why it matters" current={p.habit.description} placeholder="What this habit means to you — one line is plenty" />
}

export function HabitBareMinimumEditor(p: EditorProps) {
  return <TextFactEditor {...p} field="minimum_viable_repetition" label="Bare minimum" current={p.habit.minimum_viable_repetition} placeholder="The smallest version that still counts — one page, five minutes" rows={1} />
}

export function HabitCueEditor(p: EditorProps) {
  return <TextFactEditor {...p} field="cue_or_anchor" label="Cue" current={p.habit.cue_or_anchor} placeholder="What it follows — after the first coffee, when the kids are down" rows={1} />
}

// ── Each time takes ────────────────────────────────────────────────────────

export function HabitDurationEditor({ habit, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [value, setValue] = useState('')
  const [custom, setCustom] = useState('')
  const reset = () => {
    const n = habit.duration_minutes ? String(habit.duration_minutes) : ''
    const inChips = DURATION_CHIPS.some((c) => c.value === n)
    setValue(inChips ? n : ''); setCustom(inChips ? '' : n)
  }
  const save = async () => {
    const n = Number(custom || value)
    if (!Number.isFinite(n) || n <= 0) throw new Error('Say how long one time takes.')
    await write(habit, { duration_minutes: Math.round(n) }, onChanged)
  }
  return (
    <InlinePopover label="Each time takes" variant={variant} className={className} onOpen={reset} onSave={save} testId="habit-edit-duration" width="w-96"
                   trigger={trigger ?? (durationWords(habit.duration_minutes) || <><Plus className="h-3 w-3" /> how long</>)}>
      <ChipRow label="How long" options={DURATION_CHIPS} value={value} onChange={(v) => { setValue(v); setCustom('') }} />
      <div className="flex items-center gap-2">
        <input type="number" min={1} max={480} value={custom} onChange={(e) => { setCustom(e.target.value); setValue('') }} placeholder="Or minutes" aria-label="Minutes" className="w-28 rounded-md border border-soft bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/30" />
        <span className="text-app-micro text-muted">minutes</span>
      </div>
    </InlinePopover>
  )
}

// ── Priority ───────────────────────────────────────────────────────

export function HabitPriorityEditor({ habit, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [value, setValue] = useState('')
  const reset = () => setValue(habit.priority || 'medium')
  const save = async () => { await write(habit, { priority: (value || 'medium') as UpdateHabitRequest['priority'] }, onChanged) }
  const current = HABIT_PRIORITY_OPTIONS.find((o) => o.value === habit.priority)?.label || 'Normal'
  return (
    <InlinePopover label="Priority" variant={variant} className={className} onOpen={reset} onSave={save} testId="habit-edit-priority" trigger={trigger ?? current}>
      <ChipRow label="Priority" options={HABIT_PRIORITY_OPTIONS} value={value} onChange={setValue} />
    </InlinePopover>
  )
}

// ── Belongs to ─────────────────────────────────────────────────────────────

export function HabitParentEditor({ habit, onChanged, trigger, variant = 'icon', className }: EditorProps) {
  const [parentType, setParentType] = useState<ParentType | ''>('')
  const [parentId, setParentId] = useState<number | string | null>(null)
  // Skills hold habits too (the ratified tree; prod 2026-09-12: three of eight habits sit under a skill).
  const reset = () => { setParentType(habit.parent_goal_id ? 'goal' : habit.parent_skill_id ? 'skill' : ''); setParentId(habit.parent_goal_id ?? habit.parent_skill_id ?? null) }
  const save = async () => {
    await write(habit, {
      parent_goal_id: parentType === 'goal' && parentId ? Number(parentId) : null,
      parent_skill_id: parentType === 'skill' && parentId ? Number(parentId) : null,
    }, onChanged)
  }
  return (
    <InlinePopover label="Belongs to" variant={variant} className={className} onOpen={reset} onSave={save} testId="habit-edit-parent" width="w-96"
                   trigger={trigger ?? (habit.parent?.title || <><Plus className="h-3 w-3" /> belongs to</>)}>
      <ParentPicker allowedTypes={['goal', 'skill']} parentType={parentType} parentId={parentId} onChange={(f) => { setParentType((f.parent_type || '') as ParentType | ''); setParentId(f.parent_id || null) }} />
    </InlinePopover>
  )
}

// ── Schedule ───────────────────────────────────────────────────────────────

export function HabitScheduleEditorPopover({ habit, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [value, setValue] = useState<HabitScheduleValue>(makeDefaultScheduleValue())
  const reset = () => setValue(scheduleFromHabit(habit))
  const save = async () => {
    await write(habit, {
      rrule: value.rrule,
      scheduled_times_local: value.scheduled_times_local,
      schedule_start_date: value.schedule_start_date,
      duration_minutes: value.duration_minutes,
      target_cadence: value.target_cadence,
      preferred_days: value.preferred_days as Weekday[],
      preferred_time_window: value.preferred_time_window as TimeWindow,
    }, onChanged)
  }
  const words = hasSchedule(habit) ? scheduleWords(habit) : null
  return (
    <InlinePopover label="When it comes round" variant={variant} className={className} onOpen={reset} onSave={save} testId="habit-edit-schedule" width="w-96"
                   trigger={trigger ?? (words || <><Plus className="h-3 w-3" /> set a schedule</>)}>
      <div className="max-h-96 overflow-y-auto pr-1">
        <HabitScheduleEditor value={value} onChange={setValue} showFollowThrough={false} />
      </div>
    </InlinePopover>
  )
}

// ── Reminders (follow-through) ─────────────────────────────────────────────

const STYLE_OPTIONS = [
  { value: 'gentle', label: 'Gently' }, { value: 'steady', label: 'Steadily' }, { value: 'persistent', label: 'Persistently' }, { value: 'strict', label: 'Firmly' },
]
const MODE_OPTIONS = [
  { value: 'hard_window', label: 'In the window before' }, { value: 'soft_window', label: 'Around the time' }, { value: 'all_day', label: 'Any time that day' },
]
const WINDOW_OPTIONS = [{ value: '30', label: 'Half an hour' }, { value: '60', label: 'An hour' }, { value: '120', label: 'Two hours' }]
const EVERY_OPTIONS = [{ value: '10', label: 'Every 10 min' }, { value: '15', label: 'Every 15 min' }, { value: '30', label: 'Every 30 min' }]
const MAX_OPTIONS = [{ value: '2', label: 'Up to 2' }, { value: '4', label: 'Up to 4' }, { value: '6', label: 'Up to 6' }]

export function HabitRemindersEditor({ habit, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [on, setOn] = useState('yes')
  const [style, setStyle] = useState('')
  const [mode, setMode] = useState('')
  const [win, setWin] = useState('')
  const [every, setEvery] = useState('')
  const [max, setMax] = useState('')
  const reset = () => {
    setOn(habit.nag_enabled === false ? 'no' : 'yes')
    setStyle(habit.nag_style || 'persistent'); setMode(habit.follow_through_mode || 'hard_window')
    setWin(String(habit.nag_window_minutes || 60)); setEvery(String(habit.nag_interval_minutes || 15)); setMax(String(habit.nag_max_per_window || 4))
  }
  const save = async () => {
    await write(habit, {
      nag_enabled: on !== 'no',
      nag_style: (style || 'persistent') as UpdateHabitRequest['nag_style'],
      follow_through_mode: (mode || 'hard_window') as UpdateHabitRequest['follow_through_mode'],
      nag_window_minutes: Number(win) || 60, nag_interval_minutes: Number(every) || 15, nag_max_per_window: Number(max) || 4,
    }, onChanged)
  }
  return (
    <InlinePopover label="Hold me to it" variant={variant} className={className} onOpen={reset} onSave={save} testId="habit-edit-reminders" width="w-96"
                   trigger={trigger ?? reminderWords(habit)}>
      <ChipRow label="Remind me" options={[{ value: 'yes', label: 'Remind me' }, { value: 'no', label: 'No reminders' }]} value={on} onChange={(v) => setOn(v || 'yes')} />
      {on !== 'no' && (
        <>
          <div><div className="mb-1 text-app-micro text-muted">How</div><ChipRow label="How" options={STYLE_OPTIONS} value={style} onChange={setStyle} /></div>
          <div><div className="mb-1 text-app-micro text-muted">When</div><ChipRow label="When" options={MODE_OPTIONS} value={mode} onChange={setMode} /></div>
          {mode !== 'all_day' && <div><div className="mb-1 text-app-micro text-muted">Window</div><ChipRow label="Window" options={WINDOW_OPTIONS} value={win} onChange={setWin} /></div>}
          <div><div className="mb-1 text-app-micro text-muted">Rhythm</div><ChipRow label="Rhythm" options={EVERY_OPTIONS} value={every} onChange={setEvery} /></div>
          <div><div className="mb-1 text-app-micro text-muted">At most</div><ChipRow label="At most" options={MAX_OPTIONS} value={max} onChange={setMax} /></div>
        </>
      )}
    </InlinePopover>
  )
}

// ── "Add" row: the facts not yet set ───────────────────────────────────────

export function HabitAddRow({ habit, onChanged }: { habit: Habit; onChanged: (habit: Habit) => void }) {
  // Related things sit together: when and how long, then what carries it
  // (bare minimum, cue, why), then where it belongs (goal, priority).
  const rhythm: React.ReactNode[] = []
  if (!hasSchedule(habit)) rhythm.push(<HabitScheduleEditorPopover key="schedule" habit={habit} onChanged={onChanged} variant="chip" />)
  if (!habit.duration_minutes) rhythm.push(<HabitDurationEditor key="duration" habit={habit} onChanged={onChanged} variant="chip" />)
  const carry: React.ReactNode[] = []
  if (!habit.minimum_viable_repetition) carry.push(<HabitBareMinimumEditor key="min" habit={habit} onChanged={onChanged} variant="chip" />)
  if (!habit.cue_or_anchor) carry.push(<HabitCueEditor key="cue" habit={habit} onChanged={onChanged} variant="chip" />)
  if (!habit.description) carry.push(<HabitWhyEditor key="why" habit={habit} onChanged={onChanged} variant="chip" />)
  const belong: React.ReactNode[] = []
  if (!habit.parent?.title && !habit.parent_goal_id && !habit.parent_skill_id) belong.push(<HabitParentEditor key="parent" habit={habit} onChanged={onChanged} variant="chip" />)
  if (habit.priority !== 'high') belong.push(<HabitPriorityEditor key="priority" habit={habit} onChanged={onChanged} variant="chip" trigger={<>priority · {HABIT_PRIORITY_OPTIONS.find((o) => o.value === habit.priority)?.label || 'Normal'}</>} />)
  const groups = [rhythm, carry, belong].filter((g) => g.length > 0)
  if (groups.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid="habit-add-details">
      <span className="text-app-micro text-muted">Add</span>
      {groups.map((g, i) => <span key={i} className="flex flex-wrap items-center gap-1.5" data-testid="habit-add-group">{g}</span>)}
    </div>
  )
}
